import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Setup from "./pages/Setup";
import MorningSummary from "./pages/MorningSummary";
import Inbox from "./pages/Inbox";
import Approvals from "./pages/Approvals";
import Tier3 from "./pages/Tier3";
import Platforms from "./pages/Platforms";
import Compose from "./pages/Compose";
import Editors from "./pages/Editors";
import DecisionLog from "./pages/DecisionLog";
import Settings from "./pages/Settings";
import Recovery from "./pages/Recovery";
import DailyBrief from "./pages/DailyBrief";
import Articles from "./pages/Articles";
import Newsletter from "./pages/Newsletter";
import Substack from "./pages/Substack";
import EveningReview from "./pages/EveningReview";
import Growth from "./pages/Growth";
import HomeContent from "./pages/HomeContent";
import { Toaster } from "./components/ui/sonner";

// Public site
import PublicLayout from "./public/PublicLayout";
import Home from "./public/Home";
import Essays from "./public/Essays";
import Excerpts from "./public/Excerpts";
import Stories from "./public/Stories";
import EssayRead from "./public/EssayRead";
import About from "./public/About";
import Contact from "./public/Contact";
import { NewsletterSignup, NewsletterConfirm, NewsletterUnsubscribe } from "./public/Newsletter";

function Protected({ children }) {
  const { author, loading, setupComplete } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono-elle text-sm"
           style={{ color: "var(--elle-ink-dim)" }}>
        Lighting the lamp…
      </div>
    );
  }
  if (setupComplete === false) return <Navigate to="/setup" replace />;
  if (!author) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public site — PublicLayout wraps these */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/essays" element={<Essays />} />
            <Route path="/essays/:slug" element={<EssayRead />} />
            <Route path="/excerpts" element={<Excerpts />} />
            <Route path="/excerpts/:slug" element={<EssayRead />} />
            <Route path="/stories" element={<Stories />} />
            <Route path="/stories/:slug" element={<EssayRead />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/newsletter" element={<NewsletterSignup />} />
            <Route path="/newsletter/confirm/:token" element={<NewsletterConfirm />} />
            <Route path="/newsletter/unsubscribe/:token" element={<NewsletterUnsubscribe />} />
          </Route>

          {/* Auth pages (no layout) */}
          <Route path="/login" element={<Login />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/recovery" element={<Recovery />} />

          {/* Private dashboard */}
          <Route path="/dashboard" element={<Protected><MorningSummary /></Protected>} />
          <Route path="/dashboard/daily-brief" element={<Protected><DailyBrief /></Protected>} />
          <Route path="/dashboard/newsletter" element={<Protected><Newsletter /></Protected>} />
          <Route path="/dashboard/substack" element={<Protected><Substack /></Protected>} />
          <Route path="/dashboard/evening" element={<Protected><EveningReview /></Protected>} />
          <Route path="/dashboard/growth" element={<Protected><Growth /></Protected>} />
          <Route path="/dashboard/inbox" element={<Protected><Inbox /></Protected>} />
          <Route path="/dashboard/approvals" element={<Protected><Approvals /></Protected>} />
          <Route path="/dashboard/tier3" element={<Protected><Tier3 /></Protected>} />
          <Route path="/dashboard/platforms" element={<Protected><Platforms /></Protected>} />
          <Route path="/dashboard/compose" element={<Protected><Compose /></Protected>} />
          <Route path="/dashboard/articles" element={<Protected><Articles /></Protected>} />
          <Route path="/dashboard/editors" element={<Protected><Editors /></Protected>} />
          <Route path="/dashboard/decision-log" element={<Protected><DecisionLog /></Protected>} />
          <Route path="/dashboard/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/dashboard/home-content" element={<Protected><HomeContent /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster theme="dark" toastOptions={{
          style: { background: "var(--elle-surface)", color: "var(--elle-ink)",
                   border: "1px solid var(--elle-border)", fontFamily: "'Work Sans', sans-serif" }
        }}/>
      </BrowserRouter>
    </AuthProvider>
  );
}
